import React from 'react';

import { Divider, Flex, Skeleton, Stack } from '@chakra-ui/react';
import { renderParentIcon } from 'components/GoalsWeight/GoalsWeightSidepanelV1';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { REVIEW_FORM_KEYS } from 'reducers/reviewFormSlice';
import { REVIEW_RESULT_VIEW, useCurrentProvider, useSelectedReviewResultView } from 'reducers/reviewResultSlice';
import { QuestionResponseStatusTag } from 'screens/performanceReview/components/newFormComponents/QuestionResponseStatusTag';
import { OverallResultScore } from 'screens/performanceReview/components/newResultComponents/OverallResultScore';
import useReviewResultParams from 'screens/performanceReview/hooks/useReviewResultParams';

import {
  Help,
  KeyResultIcon,
  KeyResultPrivate2,
  ObjectiveIcon,
  PrivateObjective,
} from 'assets/assetsV1/iconComponents';

import { AccordionButtonV2, AccordionItemV2, AccordionPanelV2, AccordionV2 } from 'uiToolkitV1/AccordionV2';
import { Paper } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useReviewResultProviderEmpInfo } from '../components/sidebar/hooks/dataHooks';
import GoalScrollableNav from './components/GoalScrollableNav';
import PeopleViewGoalScrollableNav from './components/PeopleViewGoalScrollableNav';
import { useGoalResultData } from './hooks/dataHooks';

const GoalResult = () => {
  const { employeeId, providerId, reviewId } = useReviewResultParams();
  const selectedView = useSelectedReviewResultView();
  const currentProvider = useCurrentProvider();

  const { data, isLoading } = useGoalResultData({
    employeeId,
    providerId,
    responseId: currentProvider.reviewId, // <- will be used to show the submitted
    reviewId,
    // used in case of preview
    providerType: currentProvider.providerType,
  });

  const { mergedGoalQuestions, employee: firstResponseEmp } = data ?? {};
  const providerInfo = useReviewResultProviderEmpInfo({ empId: firstResponseEmp?.id });

  const firstResponseEmp1 = {
    ...firstResponseEmp,
    providerType: providerInfo?.providerType,
    peerType: providerInfo?.peerType,
  };

  const { moduleName } = useOrganisationSettings();

  if (isLoading) {
    return (
      <Paper
        bg="#F5F7F9"
        p="16px"
        sx={{
          boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.25)',
        }}
        mb="12px"
      >
        <Stack>
          <Skeleton height="20px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
        </Stack>
      </Paper>
    );
  }

  if (mergedGoalQuestions?.length === 0) return null;

  return (
    <Paper
      bg="#F5F7F9"
      p="16px"
      sx={{
        boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.25)',
      }}
      mb="12px"
    >
      <Flex alignItems="center">
        <Flex p="0 8px" flexDir="column">
          <TextBodyMedium bold>{moduleName} Review</TextBodyMedium>
          <TextLabel color="text.g2">
            {mergedGoalQuestions?.length} {mergedGoalQuestions?.length === 1 ? 'Goal' : 'Goals'}
          </TextLabel>
        </Flex>
        <OverallResultScore reviewForm={REVIEW_FORM_KEYS.GOAL} />
      </Flex>
      <Divider color="#CFD7DF" opacity={1} my="10px" />
      <Paper p="0" overflow="hidden" w="100%" bg="#FFFFFF">
        <AccordionV2>
          {mergedGoalQuestions?.map(({ questions = [], goal, okr }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <AccordionItemV2 key={index}>
              {/* just another UX inconsistency, doing it for now! */}
              <AccordionButtonV2 sx={{ background: '#FFFFFF !important' }}>
                <Flex justifyContent="space-between" alignItems="center" flexGrow={1}>
                  <Flex flexGrow={1} mr={4} flexDir="column">
                    {/* if goal is present then we will show parentOKRBasicDetails else show okr */}
                    {goal && (
                      <Flex alignItems="center">
                        {renderParentIcon(okr?.parentGoalBasicDetails?.goalType)}
                        <TextLabel color="text.g2" textAlign="left">
                          {okr?.parentGoalBasicDetails?.goalTitle ?? 'No parent'}
                        </TextLabel>
                      </Flex>
                    )}
                    <Flex>
                      {okr === null && <Help color="icon.g2" boxSize="24px" mr={2} />}
                      {okr?.type === 'OBJECTIVE' &&
                        (okr?.visibilityType === 'PRIVATE' ? (
                          <PrivateObjective color="functional.7651BC" boxSize="24px" mr={2} />
                        ) : (
                          <ObjectiveIcon color="functional.7651BC" boxSize="24px" mr={2} />
                        ))}
                      {okr?.type === 'GOAL' &&
                        (okr?.visibilityType === 'PRIVATE'
                          ? okr !== null && <KeyResultPrivate2 color="functional.7651BC" boxSize="24px" mr={2} />
                          : okr !== null && <KeyResultIcon color="functional.7651BC" boxSize="24px" mr={2} />)}
                      <TextBodyMedium bold textAlign="left">
                        {/*  in case of question without okr there would be only one question in the array - questions */}
                        {okr ? okr.title : `${questions?.[0]?.description}`}
                      </TextBodyMedium>
                    </Flex>
                  </Flex>
                  {questions.some((ques) => ques.answerType !== 'NONE') &&
                    selectedView === REVIEW_RESULT_VIEW.PEOPLE && (
                      <Flex>
                        <QuestionResponseStatusTag questions={questions} isValidateForm={false} showIcon={false} />
                      </Flex>
                    )}
                </Flex>
              </AccordionButtonV2>
              {questions.map((goalQuestion) => (
                <AccordionPanelV2 borderTop="0 !important">
                  <Flex flexGrow={1} flexDir="column" maxWidth="100%">
                    <TextBodyMedium bold textAlign="left" p={3}>
                      {goalQuestion?.description}
                    </TextBodyMedium>
                    {selectedView === REVIEW_RESULT_VIEW.PEOPLE ? (
                      <PeopleViewGoalScrollableNav
                        questionText={goalQuestion.description}
                        questionId={goalQuestion?.id}
                        goalId={goal?.id}
                        okr={okr}
                        {...goalQuestion}
                      />
                    ) : (
                      <GoalScrollableNav
                        questionText={goalQuestion.description}
                        questionId={goalQuestion?.id}
                        goalId={goal?.id}
                        question={goalQuestion}
                        okr={okr}
                        firstResponseEmp={firstResponseEmp1}
                      />
                    )}
                  </Flex>
                </AccordionPanelV2>
              ))}
            </AccordionItemV2>
          ))}
        </AccordionV2>
      </Paper>
    </Paper>
  );
};

export default GoalResult;
